import { useMemo } from 'react';
import { CAPABILITY_PACK, hasPack } from '@wapharma/domain';
import { useAuth } from '../contexts/AuthContext';

export const ADMIN_STAFF_ROLES = [
  'SUPER_ADMIN',
  'INVENTORY',
  'SALES',
  'WEB',
  'CATALOG',
  'VIEWER',
  'ACCOUNTING',
] as const;

export type AdminStaffRole = (typeof ADMIN_STAFF_ROLES)[number];

export function useStaffRoles() {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const packs = user?.packs;

  return useMemo(() => {
    const hasAny = (...allowed: string[]) => allowed.some((r) => roles.includes(r));
    const packOk = (p: (typeof CAPABILITY_PACK)[keyof typeof CAPABILITY_PACK]) =>
      hasPack(packs, roles, p);

    return {
      roles,
      packs: packs ?? [],
      hasAny,
      isSuperAdmin: roles.includes('SUPER_ADMIN'),
      canWriteCatalog:
        packOk(CAPABILITY_PACK.CATALOG_MAINTAIN) || packOk(CAPABILITY_PACK.OFFER_PRICE),
      canWriteInventory: packOk(CAPABILITY_PACK.STOCK_MOVE),
      canWriteProcurement: packOk(CAPABILITY_PACK.BUY_PROCURE),
      canWriteAccounting:
        packOk(CAPABILITY_PACK.MONEY_POST) || packOk(CAPABILITY_PACK.MONEY_READ),
      canWriteSales: packOk(CAPABILITY_PACK.SELL_COMMIT),
      canAdminParty: packOk(CAPABILITY_PACK.PARTY_ADMIN),
      canMaintainStorefront: packOk(CAPABILITY_PACK.STOREFRONT_MAINTAIN),
      canConfigureCommerce: packOk(CAPABILITY_PACK.STOREFRONT_COMMERCE),
      canPromo: packOk(CAPABILITY_PACK.OFFER_PROMO),
      canCrm: packOk(CAPABILITY_PACK.CRM_LEAD),
      canPublishContent: packOk(CAPABILITY_PACK.CONTENT_PUBLISH),
      canMutateLeadFlows: packOk(CAPABILITY_PACK.CRM_LEAD),
      canAccessCrm: packOk(CAPABILITY_PACK.CRM_LEAD),
      canReindexSearch: hasAny('SUPER_ADMIN', 'CATALOG'),
      isShowroomStaff: hasAny('SHOWROOM_STAFF'),
      isStorefront: hasAny('STOREFRONT'),
      isWeb: hasAny('WEB'),
    };
  }, [roles, packs]);
}
